import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Ic_closed_eye from "../../../src/assets/images/icons/closedEye.svg";
import Ic_eye from "../../../src/assets/images/icons/eye.svg";
import Ic_search from "../../../src/assets/images/icons/search.svg";
import { get } from "lodash";
import { useToast } from "../../Services/ToastContext";

interface GeneralInputProps {
  type?:
    | "text"
    | "password"
    | "textarea"
    | "search"
    | "number"
    | "email"
    | "time"
    | "url";
  name: string;
  label?: string;
  placeholder?: string;
  registerOptions?: any;
  required?: boolean;
  numericOnly?: boolean;
  englishOnly?: boolean;
  arabicOnly?: boolean;
  isConditionMet?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  charactersOnly?: boolean;
  showButton?: string;
  maxLength?: number;
  minLength?: number;
  customStyleClasses?: string;
  oneLanguageOnly?: boolean;
  displayInternalLabel?: boolean;
  showErrorMessage?: boolean;
  onFocus?: any;

  value?: string;
  onButtonClick?: () => void;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | any>,
  ) => void;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const GeneralInput: React.FC<GeneralInputProps> = ({
  type = "text",
  name,
  label,
  placeholder,
  registerOptions,
  required = false,
  numericOnly = false,
  englishOnly = false,
  arabicOnly = false,
  charactersOnly = false,
  isConditionMet = false,
  readOnly = false,
  disabled = false,
  showButton = "",
  maxLength,
  minLength,
  onButtonClick,
  onKeyDown,
  onChange,
  showErrorMessage = true,
  customStyleClasses,
  displayInternalLabel = true,
  oneLanguageOnly = false,
  value,
  onFocus,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    formState,
    setValue,
    watch,
    setError,
    clearErrors,
    control,
  } = useFormContext();

  const { t } = useTranslation();
  const { showToast } = useToast();
  const inputValue = watch(name);
  const [isFocused, setIsFocused] = useState(false);

  const isEnglishOnly = type === "password" || englishOnly;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let { value } = e.target;

    let invalidCharacter = false;
    let regex: RegExp | null = null;
    let replacementRegex: RegExp | null = null;
    let errorMessageKey = "";

    // If `oneLanguageOnly` is true, enforce single language input (English or Arabic)
    if (oneLanguageOnly) {
      const hasEnglish = /[a-zA-Z]/.test(value);
      const hasArabic = /[\u0600-\u06FF]/.test(value);

      // Check if both English and Arabic characters are mixed in the input
      if (hasEnglish) {
        regex = /[^a-zA-Z\s]/;
        replacementRegex = /[^a-zA-Z\s]/g;
        errorMessageKey = "only_english_characters_allowed";
      } else if (hasArabic) {
        regex = /[^\u0600-\u06FF\s]/;
        replacementRegex = /[^\u0600-\u06FF\s]/g;
        errorMessageKey = "only_arabic_characters_allowed";
      }
    } else {
      // Normal input handling for both languages or other restrictions
      if (numericOnly) {
        // Allow only numbers
        regex = /[^0-9]/;
        replacementRegex = /[^0-9]/g;
        errorMessageKey = "only_numbers_allowed";
      } else if (charactersOnly) {
        if (arabicOnly) {
          // Allow only Arabic letters and spaces
          regex = /[^\u0600-\u06FF\s]/;
          replacementRegex = /[^\u0600-\u06FF\s]/g;
          errorMessageKey = "only_arabic_characters_allowed";
        } else if (isEnglishOnly) {
          // Allow only English letters and spaces
          regex = /[^a-zA-Z\s]/;
          replacementRegex = /[^a-zA-Z\s]/g;
          errorMessageKey = "only_english_characters_allowed";
        } else {
          // Allow both English and Arabic letters and spaces
          regex = /[^a-zA-Z\u0600-\u06FF\s]/;
          replacementRegex = /[^a-zA-Z\u0600-\u06FF\s]/g;
          errorMessageKey = "only_characters_allowed";
        }
      } else if (isEnglishOnly) {
        regex = /[^a-zA-Z0-9\s.,!?'"@#%&*()\-_=+\[\]{};:/\\|<>~$^]/;
        replacementRegex = /[^a-zA-Z0-9\s.,!?'"@#%&*()\-_=+\[\]{};:/\\|<>~$^]/g;
        errorMessageKey = "only_english_characters_allowed";
      } else if (arabicOnly) {
        regex = /[^\u0600-\u06FF0-9\s]/;
        replacementRegex = /[^\u0600-\u06FF0-9\s]/g;
        errorMessageKey = "only_arabic_characters_allowed";
      }
    }

    if (regex && replacementRegex) {
      if (regex.test(value)) {
        invalidCharacter = true;
      }
      value = value.replace(replacementRegex, "");
    }

    setValue(name, value);

    if (invalidCharacter) {
      showToast(t(errorMessageKey), "error");
      setError(name, {
        type: "invalidCharacter",
        message: t(errorMessageKey),
      });
    } else {
      clearErrors(name);
    }

    if (onChange) {
      onChange(e);
    }
  };

  const getPlaceholderWithAsterisk = (
    placeholderText: string,
    isRequired: boolean,
  ) => {
    return isRequired ? `${placeholderText} *` : placeholderText;
  };

  const error = get(formState.errors, name);
  const errorMessage =
    error && error.message
      ? typeof error.message === "string"
        ? error.message
        : String(error.message)
      : undefined;
  const shouldDisplayErrorInline = error?.type !== "invalidCharacter";

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.preventDefault();

    const input = e.target;
    let errorMessage = "";

    if (input.validity.typeMismatch) {
      if (type === "email") {
        errorMessage = t("invalid_email_address");
      } else if (type === "url") {
        errorMessage = t("invalid_website_format");
      }
    } else if (input.validity.patternMismatch && type === "url") {
      errorMessage = t("invalid_website_format");
    } else if (input.validity.valueMissing && required) {
      errorMessage = t("required_field");
    }

    if (errorMessage) {
      showToast(errorMessage, "error");
      input.setCustomValidity(errorMessage);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity("");
  };
const hasValue=inputValue!==undefined && inputValue!==null
  return (
    <div className="relative w-full max-w-[95dvw] !min-h-11">
      {displayInternalLabel && hasValue && (
        <label
          htmlFor={name}
          className={`absolute ${isFocused ? "!text-primary" : ""} start-2 top-0.5 z-[3] bg-white px-1 text-sm transition-all duration-200 ${
            hasValue  
              ? "-translate-y-2.5 text-xs text-primary-text"
              : "text-primary-text"
          }`}
        >
          {label}
        </label>
      )}
      {type === "textarea" ? (
        <div className={`relative w-full`}>
          <textarea
            id={name}
            className={`${customStyleClasses} h-40 w-full min-w-60 appearance-none rounded-lg border border-systemBg px-4 py-2 text-base text-primary-text placeholder-body-text focus:!border-primary focus:bg-white focus:outline-none ${readOnly ? "bg-primary-bg text-body-text" : "bg-white"} ${errorMessage ? "!border-2 !border-error bg-white" : ""}`}
            placeholder={getPlaceholderWithAsterisk(
              placeholder || "",
              required || false,
            )}
            maxLength={maxLength ?? 250}
            minLength={minLength}
            {...register(name, {
              required,
              minLength: minLength
                ? {
                    value: minLength,
                    message: t("min_length_error", { count: minLength }),
                  }
                : undefined,
              ...registerOptions,
            })}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            readOnly={readOnly}
            disabled={disabled}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={() => setIsFocused(false)}
          />
        </div>
      ) : (
        <div
          className={`relative ${type === "search" ? "w-full" : ""}`}
        >
          <Controller
            name={name}
            control={control}
            rules={{
              required: required ? t("required_field") : false,
              minLength: minLength
                ? {
                    value: minLength,
                    message: t("min_length_error", { count: minLength }),
                  }
                : undefined,
              ...registerOptions,
            }}
            render={({ field }) => (
              <input
                {...field}
                autoComplete="off"
                type={
                  type === "password"
                    ? !showPassword
                      ? "password"
                      : "text"
                    : type
                }
                id={name}
                className={`${customStyleClasses} w-full appearance-none rounded-lg ${
                  type === "search" ? "indent-5" : ""
                } !h-11 min-w-60 border border-systemBg px-4 py-2 text-base text-primary-text placeholder-body-text focus:!border-primary focus:bg-white focus:outline-none ${readOnly ? "bg-primary-bg text-body-text" : "bg-white"} ${errorMessage ? "!border-2 !border-error bg-white" : ""}`}
                placeholder={getPlaceholderWithAsterisk(
                  placeholder || "",
                  required || false,
                )}
                maxLength={maxLength ?? 50}
                onChange={handleInputChange}
                onFocus={(e) => {
                  setIsFocused(true);
                  onFocus?.(e);
                }}
                onBlur={() => setIsFocused(false)}
                onKeyDown={onKeyDown}
                readOnly={readOnly}
                disabled={disabled}
              />
            )}
          />
          {type === "password" && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 end-3 flex items-center pe-3 text-sm leading-5"
            >
              {showPassword ? (
                <img src={Ic_eye} alt="eye" />
              ) : (
                <img src={Ic_closed_eye} alt="eye" />
              )}
            </button>
          )}
          {type === "search" && (
            <img
              src={Ic_search}
              alt="search"
              className="absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer"
              onClick={onButtonClick}
            />
          )}
        </div>
      )}
      {showButton && (
        <button
          type="button"
          onClick={onButtonClick}
          className="absolute end-2 top-2 text-lg font-bold capitalize text-primary"
        >
          {showButton}
        </button>
      )}
      {showErrorMessage && errorMessage && shouldDisplayErrorInline && (
        <p
          className={`mt-1 text-xs ${
            isConditionMet ? "text-successGreen" : "text-error"
          }`}
        >
          {errorMessage === "Expected string, received null"
            ? t("required")
            : errorMessage}
        </p>
      )}
    </div>
  );
};

export default GeneralInput;
